import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { saveDatenadapterCredentials } from "@/services/datenadapter/saveDatenadapterCredentials";
import { getDatenadapterCredentials } from "@/services/datenadapter/getDatenadapterCredentials";
import DatenadapterStore from "@/stores/DatenadapterStore";

export function useDatenadapterCredentials() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Use the Datenadapter store
  const localUsername = DatenadapterStore(state => state.localUsername);
  const localPassword = DatenadapterStore(state => state.localPassword);
  const localApiUrl = DatenadapterStore(state => state.localApiUrl);
  const hasChanges = DatenadapterStore(state => state.hasChanges);
  const setLocalUsername = DatenadapterStore(state => state.setLocalUsername);
  const setLocalPassword = DatenadapterStore(state => state.setLocalPassword);
  const setLocalApiUrl = DatenadapterStore(state => state.setLocalApiUrl);
  const setOriginalUsername = DatenadapterStore(state => state.setOriginalUsername);
  const setOriginalPassword = DatenadapterStore(state => state.setOriginalPassword);
  const setOriginalApiUrl = DatenadapterStore(state => state.setOriginalApiUrl);

  const { data: credentials, isLoading } = useQuery({
    queryKey: ["datenadapterCredentials"],
    queryFn: getDatenadapterCredentials,
  });

  // Sync store with query data when it changes
  useEffect(() => {
    if (credentials) {
      setOriginalUsername(credentials.username);
      setOriginalPassword(credentials.password);
      setOriginalApiUrl(credentials.apiUrl);
    }
  }, [credentials, setOriginalUsername, setOriginalPassword, setOriginalApiUrl]);

  const { mutate: saveCredentialsMutation, isPending: isSaving } = useMutation({
    mutationFn: () => saveDatenadapterCredentials(localUsername, localPassword, localApiUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datenadapterCredentials"] });
      // Reset changes after successful save
      setOriginalUsername(localUsername);
      setOriginalPassword(localPassword);
      setOriginalApiUrl(localApiUrl);
    },
  });

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalUsername(e.target.value.trim());
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPassword(e.target.value);
  };

  const handleApiUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiUrl(e.target.value.trim());
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSaveCredentials = () => {
    // Ensure no leading/trailing spaces before saving
    setLocalUsername(localUsername.trim());
    setLocalApiUrl(localApiUrl.trim());
    saveCredentialsMutation();
  };

  return {
    username: localUsername,
    password: localPassword,
    apiUrl: localApiUrl,
    isLoading: isLoading || isSaving,
    showPassword,
    hasChanges,
    handleUsernameChange,
    handlePasswordChange,
    handleApiUrlChange,
    togglePasswordVisibility,
    handleSaveCredentials,
  };
}
